// src/components/navigation/Sidebar.tsx
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Phone,
  Bot,
  Link as LinkIcon,
  User,
  LogOut,
  Mail,
  Map,
  ListTodo,
  MessageSquareCode,
  CreditCard,
  Bell,
  Clock,
  Zap,
  Users,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useBranding } from '@/contexts/BrandingContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import FoundingMemberBadge from '@/components/ui/FoundingMemberBadge';
import { isPartnerUser } from '@/utils/partnerUtils';
import { useUnreadAlerts } from '@/hooks/useUserAlerts';
import { useBillingUsage } from '@/hooks/useBilling';
import MemoizedLogo from '@/components/branding/MemoizedLogo';

const baseNavigation = [
  { name: 'Dashboard', href: '/dashboard/dashboard', icon: LayoutDashboard },
  { name: 'Scheduled Calls', href: '/dashboard/call-queue', icon: ListTodo },
  { name: 'Past Calls', href: '/dashboard/calls', icon: Phone },
  { name: 'Email Management', href: '/dashboard/emails', icon: Mail },
  { name: 'AI Employee Settings', href: '/dashboard/receptionist-settings', icon: Bot },
  { name: 'Hero Commands™', href: '/dashboard/hero-commands', icon: MessageSquareCode },
  { name: 'Integrations', href: '/dashboard/integrations', icon: LinkIcon },
  { name: 'Billing', href: '/dashboard/billing', icon: CreditCard },
  { name: 'Account', href: '/dashboard/account', icon: User },
  { name: 'Resources', href: '/dashboard/resources', icon: Map },
];

export default function Sidebar() {
  const { signOut, user, isFoundingMember, profile } = useAuth();
  const { branding, isWhiteLabel } = useBranding();
  const navigate = useNavigate();
  const { data: alerts } = useUnreadAlerts();
  const { data: billingUsage } = useBillingUsage();

  const handleLogoutClick = async () => {
    try {
      const { error } = await signOut();
      if (error) {
        throw error;
      }
      toast.success('Logged out successfully');
      navigate('/login', { replace: true });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Logout failed: ${errorMessage}`);
      console.error('Logout error from sidebar:', error);
    }
  };

  const userDisplayName = profile?.full_name || user?.email;
  const unreadCount = alerts?.length || 0;

  const isPartner = isPartnerUser(profile?.user_type);

  // Derive visible navigation based on partner status
  const navigation = isPartner
    ? [
        { name: 'Partner', href: '/partner/dashboard', icon: Users },
        { name: 'Billing', href: '/dashboard/billing', icon: CreditCard },
        { name: 'Account', href: '/dashboard/account', icon: User },
        { name: 'Resources', href: '/dashboard/resources', icon: Map },
      ]
    : baseNavigation;

  return (
    <div className="flex flex-col h-full w-64 bg-muted border-r border-border">
      <div className="flex items-center justify-center px-4 py-0 border-b border-border">
        <MemoizedLogo
          logoUrl={branding.logoUrl}
          companyName={branding.companyName}
          isWhiteLabel={isWhiteLabel}
          className="h-40 w-auto max-w-full object-contain"
        />
      </div>
      <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
        {navigation.map((item) => (
          <NavLink
            key={item.name}
            to={item.href}
            end
            className={({ isActive }) =>
              `flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${
                isActive
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-[#7087a9] hover:bg-accent'
              }`
            }
          >
            <item.icon className="mr-3 h-5 w-5 flex-shrink-0" aria-hidden="true" />
            <span>{item.name}</span>
          </NavLink>
        ))}
      </nav>

      <div className="p-4 border-t border-border">
        {/* Free Trial Indicator */}
        {billingUsage?.freeTrialInfo?.isInFreeTrial && (
          <div className={`rounded-lg p-3 mb-3 border ${
            (billingUsage.freeTrialInfo.daysRemaining || 0) <= 3 
              ? "bg-orange-50 border-orange-200 dark:bg-orange-950/20 dark:border-orange-800" 
              : "bg-blue-50 border-blue-200 dark:bg-blue-950/20 dark:border-blue-800"
          }`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center">
                <Zap className={`h-4 w-4 mr-2 ${
                  (billingUsage.freeTrialInfo.daysRemaining || 0) <= 3 ? "text-orange-500" : "text-blue-500"
                }`} />
                <span className="text-sm font-medium">
                  {billingUsage.freeTrialInfo.trialType === 'stripe' ? 'Trial Active' : 'Free Credits'}
                </span>
              </div>
              <Badge 
                variant={
                  (billingUsage.freeTrialInfo.daysRemaining || 0) <= 3 ? "destructive" : "secondary"
                } 
                className="text-xs"
              >
                {billingUsage.freeTrialInfo.daysRemaining || 0} days left
              </Badge>
            </div>
            <div className="flex items-center text-xs text-muted-foreground">
              <Clock className="h-3 w-3 mr-1" />
              {billingUsage.freeTrialInfo.trialType === 'stripe' 
                ? 'Plan activates automatically'
                : 'Choose plan to continue'
              }
            </div>
          </div>
        )}

        {userDisplayName && (
          <div className="bg-accent/50 rounded-lg p-3 mb-3 transition-all duration-200 hover:bg-accent/70">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center text-sm font-medium text-foreground mb-1">
                  <div className="h-2 w-2 bg-green-500 rounded-full mr-2 flex-shrink-0"></div>
                  <span className="truncate">{userDisplayName}</span>
                </div>
                <div className="flex items-center text-xs text-muted-foreground">
                  <Mail className="w-3 h-3 mr-1.5 flex-shrink-0" />
                  <span className="truncate">Online</span>
                </div>
                {isFoundingMember && (
                  <div className="mt-2">
                    <FoundingMemberBadge className="text-xs px-2 py-0.5" />
                  </div>
                )}
              </div>
              <div className="ml-3">
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative h-9 w-9 text-muted-foreground hover:text-foreground hover:bg-background/80 transition-all duration-200"
                  id="sidebar-notification-bell"
                >
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <div className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
                      <span className="text-xs font-semibold text-white">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    </div>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
        <Button
          variant="ghost"
          className="w-full justify-start text-muted-foreground hover:text-foreground hover:bg-accent/70 transition-all duration-200"
          onClick={handleLogoutClick}
        >
          <LogOut className="mr-3 h-5 w-5" aria-hidden="true" />
          Logout
        </Button>
      </div>
    </div>
  );
}